import { useCallback, useContext, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import AddIcon from '@mui/icons-material/Add'
import Transform from '@mui/icons-material/Transform'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import { useQuery } from '@tanstack/react-query'
import { object, node, bool } from 'prop-types'
import { useSelector } from 'react-redux'
import uuid from 'uuid/v4'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import {
  CvsTextField,
  CvsAutoComplete,
  CvsCardList,
  CvsImmediateActionDropdownButton,
  CvsChipBuilderInput,
} from '@components'
import { formatSelectedOptions } from '@components/select/cvs-autocomplete'
import { useCreativeClassifications } from '@global/hooks/queries/useCreativeClassifications'
import { api } from 'api'
import { templateCopyContext } from '../context'
import { TemplateFieldCard } from '../template-field-card'
import {
  FullWidth,
  NoMarginTypography,
  TypographyBold,
} from './components/integration-form-manager/integration-form-fields/integration-filters/styled-components'
import { TextOptionSelector } from './components/utils/text-option-selector'
import { t } from '@lingui/core/macro'

const Block = styled.div`
  width: 100%;
`

const Flex = styled.div`
  display: inline-flex;
`

const FlexInline = styled(Block)`
  display: inline-flex;
  justify-content: space-between;
`

const AutocompleteWidth = styled(CvsAutoComplete)`
  width: 220px;
  margin-top: -8px;
  margin-right: 12px;
`

const MarginBottom = styled(FlexInline)`
  margin-top: 12px;
  margin-bottom: 12px;
`

const FlexRadioInline = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
`

const LessMarginTop = styled(CvsImmediateActionDropdownButton)`
  margin-top: -24px;
  padding-left: 12px;
  padding-right: 12px;
`

const Auto = styled(CvsAutoComplete)`
  width: 120px;
  max-width: 120px;
  margin-top: -8px;
  margin-right: 12px;
`

const CheckboxUp = styled(FormControlLabel)`
  margin-top: -12px;
  margin-left: 12px;
`

const delimiters = [
  { label: ',', value: ',' },
  { label: ';', value: ';' },
  { label: 'tab', value: '\t' },
  { label: 'space', value: ' ' },
]

const downloadTypes = {
  csv: 'csv',
  xlsx: 'xlsx',
  json: 'json',
}

const filenameOptions = [
  { id: '{{template_name}}', label: 'Template' },
  { id: '{{submitted_at}}', label: 'Submitted at' },
  { id: '{{submitted_by}}', label: 'Submitted By' },
]

export const FileFormPartial = ({
  assignment = {},
  children = null,
  isFileBased = true,
  shouldIncludeName = true,
  enablePerTarget = false,
}) => {
  const { template, onChange } = useContext(templateCopyContext)
  const [showPatternModal, setShowPatternModal] = useState(false)
  const [selectedPattern, setSelectedPattern] = useState(null)
  const [selectedSegments, setSelectedSegments] = useState([])
  const { data: classifications = [] } = useCreativeClassifications()
  const [
    CREATIVE_CLASSIFICATION_ENABLED = false,
    TEXT_SELECTION_OUTBOUND = false,
    ROW_PROPERTIES_OUTBOUND_ENABLED = false,
  ] = useSelector(
    selectFeatureIncludes([
      FeatureDict.CREATIVE_CLASSIFICATION,
      FeatureDict.TEXT_SELECTION_OUTBOUND,
      FeatureDict.ROW_PROPERTIES_OUTBOUND,
    ]),
  )

  const { data: targets } = useQuery({
    queryKey: ['targetsForFields', Object.keys(template?.fieldsById)],
    queryFn: async () => {
      const fieldIds = Object.keys(template?.fieldsById)
      const response = await api.post(`/api/v1/account/field/targets`, { fieldIds })

      return response.data.map(target => ({
        label: target.name,
        value: target.id,
      }))
    },
    enabled: enablePerTarget,
  })

  const nameOptions = [
    {
      value: '{{template_name}}',
      display_name: t`Template Name`,
    },
    {
      value: '{{submission_name}}',
      display_name: t`Submission Name`,
    },
    {
      value: '{{submitted_at}}',
      display_name: t`Submitted At`,
    },
    {
      value: '{{submitted_by}}',
      display_name: t`Submitted By`,
    },
  ]

  const patterns = useSelector(state => {
    const pats = Object.values(state.Template.templateFieldsById).reduce((memo, item) => {
      // if it's a pattern, or a segment field
      if (item?.field?.is_pattern) {
        memo[item.field.id] = item.field
      }
      return memo
    }, {})
    return pats
  })

  const onTemplateChange = useCallback(
    (name, value) => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          [name]: value,
        },
      }))
    },
    [assignment.id, onChange],
  )

  const onMetadataChange = useCallback(
    (name, value) =>
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          metadata: {
            ...old[assignment.id].metadata,
            [name]: value,
          },
        },
      })),
    [onChange, assignment],
  )

  const childChanged = outputField => {
    // TODO: super bad performance
    const childIndex = assignment.included_output_fields.findIndex(iof => iof.id === outputField.id)
    const newChildren = [...assignment.included_output_fields]
    newChildren.splice(childIndex, 1, outputField)
    onChange(old => ({
      ...old,
      [assignment.id]: {
        ...old[assignment.id],
        included_output_fields: newChildren,
      },
    }))
  }

  const changeOrder = useCallback(
    newOrder => {
      onChange(old => ({
        ...old,
        [newOrder.id]: newOrder,
      }))
    },
    [onChange],
  )
  const changeAllLabels = useCallback(
    fxn => {
      const output = assignment.included_output_fields
      onTemplateChange('included_output_fields', output.map(fxn))
    },
    [onTemplateChange, assignment, template],
  )

  const addMetaField = type => {
    if (type === 'Pattern') {
      // pop the modal which allows you to select pattern, and then chip elements of segements of pattern.
      setShowPatternModal(true)
    } else {
      onTemplateChange('included_output_fields', [
        ...assignment.included_output_fields,
        {
          id: uuid(),
          name: type,
          display: type,
          selected: true,
          isMeta: true,
          fieldType: 'meta_field',
        },
      ])
    }
  }

  const addClaravineAIField = type => {
    onTemplateChange('included_output_fields', [
      ...assignment.included_output_fields,
      {
        id: type.templateClassificationId || uuid(),
        name: type.categoryText,
        display: type.categoryText,
        selected: true,
        isMeta: true,
        fieldType: 'classification_field',
      },
    ])
  }

  const createPatternField = () => {
    onTemplateChange('included_output_fields', [
      ...assignment.included_output_fields,
      {
        id: uuid(),
        name: 'Pattern',
        display: t`Pattern`,
        selected: true,
        isMeta: true,
        patternFieldSegments: selectedSegments,
        patternId: selectedPattern,
      },
    ])
    setSelectedPattern(null)
    setSelectedSegments([])
    setShowPatternModal(false)
  }

  const { creativeConfiguration = [] } = template?.template || {}

  const claravineAiFields = creativeConfiguration.map(
    ({ templateClassificationId, categoryId }) => ({
      templateClassificationId,
      categoryId,
      categoryText: classifications.find(({ id }) => id === categoryId)?.categoryText,
    }),
  )

  const autocompleteOptions = Object.values(downloadTypes).map(t => ({ label: t, value: t }))
  const patternOptions = selectedPattern
    ? patterns[selectedPattern].segments.map(item => ({
        value: item.id,
        label: item.name,
      }))
    : []

  const outboundRowProperties = useMemo(
    () =>
      ROW_PROPERTIES_OUTBOUND_ENABLED
        ? {
            title: t`Properties`,
            options: [
              'Template Name',
              'Submission Name',
              'Submitter Name',
              'Submitter Email',
              'Created By',
              'Created Date',
              'Submission Created Date',
              'Updated Date',
              'Empty',
              'Pattern',
            ].map(opt => ({
              title: opt,
              action: () => addMetaField(opt),
            })),
          }
        : {
            title: t`Meta field`,
            options: [
              'Template Name',
              'Submitter Name',
              'Submitter Email',
              'Created Date',
              'Updated Date',
              'Empty',
              'Pattern',
            ].map(opt => ({
              title: opt,
              action: () => addMetaField(opt),
            })),
          },
    [ROW_PROPERTIES_OUTBOUND_ENABLED, addMetaField],
  )

  return assignment ? (
    <>
      {shouldIncludeName && (
        <CvsTextField
          label={t`Name`}
          name="name"
          fullWidth
          onChange={({ target }) =>
            onChange(old => ({
              ...old,
              [assignment.id]: { ...old[assignment.id], name: target.value },
            }))
          }
          value={assignment.name ?? ''}
        />
      )}
      {children}
      <Block>
        {!isFileBased ? null : (
          <>
            <FlexInline>
              {TEXT_SELECTION_OUTBOUND ? (
                <TextOptionSelector
                  assignment={assignment}
                  placeholder={t`File name`}
                  label={t`File name`}
                  metadataKey="filename_pattern"
                  nameOptions={nameOptions}
                />
              ) : (
                <CvsChipBuilderInput
                  options={filenameOptions}
                  style={{ marginRight: 8 }}
                  label={t`Filename`}
                  value={assignment.filename_pattern ?? ''}
                  onChange={value => onTemplateChange('filename_pattern', value)}
                />
              )}
            </FlexInline>
            <Flex>
              <AutocompleteWidth
                label={t`File types`}
                isMulti
                options={autocompleteOptions}
                onSelected={value => onTemplateChange('file_type', value)}
                isClearable={true}
                selected={formatSelectedOptions(true, assignment.file_type, autocompleteOptions)}
                data-testid="file-type-select"
              />
              <Auto
                selected={assignment.delimiter}
                label={t`Delimiter`}
                onChange={({ value }) => onTemplateChange('delimiter', value)}
                options={delimiters}
              />
              <CheckboxUp
                label={t`Include summary header`}
                control={
                  <Checkbox
                    checked={!!assignment.include_summary_header}
                    value={Boolean(assignment.include_summary_header)}
                    onChange={({ target: { checked } }) =>
                      onTemplateChange('include_summary_header', checked ? 1 : 0)
                    }
                  />
                }
              />
            </Flex>
          </>
        )}
        <MarginBottom>
          <FormControl variant="standard" component="fieldset">
            <FlexRadioInline
              value={assignment.report_type ?? 'default'}
              onChange={({ target: { value } }) => onTemplateChange('report_type', value)}
            >
              <FormControlLabel value="custom" control={<Radio />} label={t`Custom`} />
              <FormControlLabel value="default" control={<Radio />} label={t`Default summary`} />
              {enablePerTarget && (
                <FormControlLabel value="perTarget" control={<Radio />} label={t`Per target`} />
              )}
            </FlexRadioInline>
          </FormControl>
          {assignment.report_type === 'custom' && (
            <div style={{ display: 'flex', gap: '15px' }}>
              <LessMarginTop
                title={t`Transform`}
                style={{ paddingLeft: 12, paddingRight: 12, backgroundColor: '#01A166' }}
                icon={<Transform />}
                options={[
                  {
                    title: t`replace spaces with underscores`,
                    action: () =>
                      changeAllLabels(item => ({
                        ...item,
                        display: item.display.replace(/ /g, '_'),
                      })),
                  },
                  {
                    title: t`replace underscores with spaces`,
                    action: () =>
                      changeAllLabels(item => ({
                        ...item,
                        display: item.display.replace(/_/g, ' '),
                      })),
                  },
                  {
                    title: t`to lowercase`,
                    action: () =>
                      changeAllLabels(item => ({ ...item, display: item.display.toLowerCase() })),
                  },
                  {
                    title: t`to uppercase`,
                    action: () =>
                      changeAllLabels(item => ({ ...item, display: item.display.toUpperCase() })),
                  },
                ]}
              />
              <LessMarginTop
                title={outboundRowProperties.title}
                style={{ paddingLeft: 12, paddingRight: 12, backgroundColor: '#01A166' }}
                icon={<AddIcon />}
                options={outboundRowProperties.options}
              />
              {CREATIVE_CLASSIFICATION_ENABLED && claravineAiFields.length > 0 ? (
                <LessMarginTop
                  title="Claravine AI"
                  style={{ paddingLeft: 12, paddingRight: 12, backgroundColor: '#6236FF' }}
                  icon={<AddIcon />}
                  options={claravineAiFields?.map(opt => ({
                    title: opt.categoryText,
                    action: () => {
                      addClaravineAIField(opt)
                    },
                  }))}
                />
              ) : null}
            </div>
          )}
        </MarginBottom>
        {assignment.report_type !== 'perTarget' && (
          <Block>
            <CvsCardList
              assignment={assignment}
              type={assignment.report_type}
              Card={rest => TemplateFieldCard({ assignment, onChange: childChanged, ...rest })}
              onOrderChange={changeOrder}
              items={assignment.included_output_fields || []}
            />
          </Block>
        )}
        {assignment.report_type === 'perTarget' && !!targets?.length && (
          <>
            <Block>
              <AutocompleteWidth
                label={t`Target`}
                options={targets}
                onSelected={value => onMetadataChange('target_id', value)}
                isClearable={true}
                selected={assignment.metadata.target_id}
              />
            </Block>
            <Block>
              <FullWidth>
                <TypographyBold variant="h4">{t`Transform columns`}</TypographyBold>
                <NoMarginTypography variant="body2">
                  {t`Select to transform how the columns will be displayed in the report.`}
                </NoMarginTypography>
              </FullWidth>
              <AutocompleteWidth
                label={t`Uppercase/Lowercase`}
                options={[
                  { label: t`Do nothing`, value: '' },
                  { label: t`To Lowercase`, value: 'to_lowercase' },
                  { label: t`To Uppercase`, value: 'to_uppercase' },
                ]}
                onSelected={value => onMetadataChange('transform_case', value)}
                isClearable={true}
                selected={assignment.metadata.transform_case}
              />
              <AutocompleteWidth
                label={t`Space/Underscore`}
                options={[
                  { label: t`Do nothing`, value: '' },
                  {
                    label: t`Replace space with underscore`,
                    value: 'replace_space_with_underscore',
                  },
                  {
                    label: t`Replace underscore with space`,
                    value: 'replace_underscore_with_space',
                  },
                ]}
                onSelected={value => onMetadataChange('transform_replace', value)}
                isClearable={true}
                selected={assignment.metadata.transform_replace}
              />
            </Block>
          </>
        )}
        {assignment.report_type === 'perTarget' && !targets?.length && (
          <Block>
            <Alert variant="filled" severity="warning">
              {t`You must select fields that are associated with a target in order to use the per target report type.`}
            </Alert>
          </Block>
        )}
      </Block>
      <Dialog
        open={showPatternModal}
        onClose={() => {
          setShowPatternModal(false)
        }}
      >
        <DialogTitle>{t`Customize pattern field`}</DialogTitle>
        <DialogContent>
          <CvsAutoComplete
            autoFocus
            margin="dense"
            label={t`Select pattern`}
            fullWidth
            options={Object.values(patterns).map(item => ({ label: item.name, value: item.id }))}
            selected={selectedPattern}
            onSelected={val => {
              setSelectedPattern(val)
            }}
          />
          <CvsAutoComplete
            isMulti={true}
            margin="dense"
            options={patternOptions}
            label={t`Select segments`}
            selected={formatSelectedOptions(true, selectedSegments, patternOptions)}
            onSelected={value => setSelectedSegments(value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowPatternModal(false)
            }}
            color="primary"
          >
            {t`Cancel`}
          </Button>
          <Button
            variant="filled"
            onClick={() => {
              createPatternField()
            }}
            color="primary"
          >
            {t`Confirm`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : null
}

FileFormPartial.propTypes = {
  integration: object,
  assignment: object,
  children: node,
  isFileBased: bool,
}
